import React, { useCallback } from "react";
import {
    List,
    ListItem,
    ListItemButton,
    Typography,
    useMediaQuery,
    useTheme,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
} from "@mui/material";
import { ALPHABET_LETTERS } from "../../../constants/directoryConstants";
import styles from "./AlphabetNavigator.module.scss";

interface AlphabetNavigatorProps {
    activeLetter: string;
    onLetterSelect: (letter: string) => void;
}

const AlphabetNavigator: React.FC<AlphabetNavigatorProps> = ({ activeLetter, onLetterSelect }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClick = useCallback(
        (letter: string) => {
            onLetterSelect(letter);
        },
        [onLetterSelect],
    );

    const handleMobileChange = useCallback(
        (event: SelectChangeEvent) => {
            onLetterSelect(event.target.value);
        },
        [onLetterSelect],
    );

    // Mobile dropdown version
    if (isMobile) {
        return (
            <FormControl className={styles.mobileSelect}>
                <Select
                    value={activeLetter}
                    onChange={handleMobileChange}
                    displayEmpty
                    variant="outlined"
                    size="small"
                >
                    {ALPHABET_LETTERS.map((letter) => (
                        <MenuItem key={letter} value={letter}>
                            {letter}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    // Desktop sidebar version
    return (
        <nav className={styles.navigator} aria-label="Alphabet navigation">
            <List dense disablePadding>
                {ALPHABET_LETTERS.map((letter) => (
                    <ListItem key={letter} disablePadding disableGutters>
                        <ListItemButton
                            onClick={() => handleClick(letter)}
                            selected={letter === activeLetter}
                            className={styles.letterItem}
                            aria-current={letter === activeLetter ? "true" : undefined}
                        >
                            <Typography variant="body1" className={styles.letter}>
                                {letter}
                            </Typography>
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </nav>
    );
};

export default AlphabetNavigator;
